import { formatDistanceToNow } from "date-fns";
import { et } from "date-fns/locale";

import { cn } from "@/lib/utils";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { ProjectActivity } from "@/domains/documents/hooks/useProjectsActivity";

interface ProjectActivityLineProps {
  /** The project's aggregates; undefined reads as no recent activity. */
  activity: ProjectActivity | undefined;
  isError?: boolean;
  className?: string;
}

function activitySummary(
  activity: ProjectActivity | undefined,
  copy: ReturnType<typeof useLanguage>["t"]["projects"]["activity"]
): string {
  const parts: string[] = [];
  if (activity && activity.uploads7d > 0) {
    parts.push(copy.uploadsThisWeek.replace("{count}", String(activity.uploads7d)));
  }
  if (activity && activity.openIssues > 0) {
    parts.push(copy.openIssues.replace("{count}", String(activity.openIssues)));
  }

  return parts.length > 0 ? parts.join(" · ") : copy.noRecentActivity;
}

/**
 * "What is happening in this project" — a muted summary (uploads this week ·
 * open issues) plus a relative last-activity line when there were uploads.
 * Shared by every project tile that shows activity, so the dashboard grid and
 * the scope chooser word it the same way.
 */
export function ProjectActivityLine({
  activity,
  isError = false,
  className,
}: ProjectActivityLineProps) {
  const { t, language } = useLanguage();
  const summary = activitySummary(activity, t.projects.activity);

  return (
    <div className={cn("text-[11px] leading-snug text-muted-foreground", className)}>
      <p>{isError ? t.projects.activity.unavailable : summary}</p>
      {!isError && activity?.lastUploadAt && (
        <p>
          {t.projects.activity.lastActivity.replace(
            "{time}",
            formatDistanceToNow(new Date(activity.lastUploadAt), {
              addSuffix: true,
              locale: language === "et" ? et : undefined,
            })
          )}
        </p>
      )}
    </div>
  );
}
